import { Demand, DemandLevel, Question, QuestionDTO, QuestionDTOPaginatedList, QuestionStatus } from '../api/schemas/schema'
import { IDataContext } from '../interfaces/IDataContext'
import { UseFormReset, UseFormReturn } from 'react-hook-form'
import { UpdateItemInCollection } from './stateHelper'
import { t } from '../i18n/i18n'
import { api } from './apiHelper'
export type QuestionFormValues = {
   selectedQuestion: QuestionDTO
   questions: QuestionDTO[]
}

/**
 * Matches RTF content that renders as nothing: only empty paragraphs, line breaks or
 * non-breaking spaces. The paragraphs may carry attributes — an emptied but indented
 * paragraph is serialised as `<p style="margin-left: 40px"></p>`.
 */
const emptyRTFRegex = /^(?:<p[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)*$/i

const normalizeEmptyRTFInput = <T extends string | null | undefined>(value: T) =>
   typeof value === 'string' && emptyRTFRegex.test(value.trim()) ? '' : value

const formatEmptyRTFInput = (formValue: QuestionDTO) => {
   formValue.text = normalizeEmptyRTFInput(formValue.text)
   formValue.backgroundInformation = normalizeEmptyRTFInput(formValue.backgroundInformation)
   formValue.proposedAction = normalizeEmptyRTFInput(formValue.proposedAction)
}
export const saveQuestion = async (
   setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
   dataContext: IDataContext,
   data: QuestionFormValues,
   reset: UseFormReset<QuestionFormValues>,
   demand: Demand,
   grandParentId: string
): Promise<void> => {
   try {
      const { accessToken } = dataContext.state
      setIsSubmitting(true)
      formatEmptyRTFInput(data.selectedQuestion)
      const res = (await api(accessToken).questionUpdate(data.selectedQuestion)).data

      const selectedQuestions = data.questions
      selectedQuestions[data.questions.findIndex((x) => x.order === res.order)] = res

      reset({ questions: selectedQuestions, selectedQuestion: res })
      updateQuestionsInState(dataContext, selectedQuestions, demand, grandParentId)
      dataContext.handleMessage(dataContext.setRootState, 'success', t('SavedSuccess'), t('QuestionSaved'))
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('SavedError'), error.message)
   } finally {
      setIsSubmitting(false)
   }
}

export const getNewQuestion = (name: string, parentId: string): Question => ({
   modifiedBy: name,
   modifiedDate: new Date().toISOString(),
   backgroundInformation: '',
   order: 0,
   valuation: 1,
   parentId,
   text: '',
   proposedAction: '',
   status: QuestionStatus.Ongoing,
   demandComplianceModules: '',
   alternativeProcesses: [],
   answerOptions: [],
   leadershipPerspectives: [],
   organisationSizes: [],
   organisationTypes: [],
   process: undefined,
   snIs: [],
   subjects: [],
   answerType: null,
})

export const deleteQuestion = async (
   setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
   dataContext: IDataContext,
   data: QuestionFormValues,
   reset: UseFormReset<QuestionFormValues>,
   demand: Demand,
   lvlOneParentDemandId: string
): Promise<void> => {
   try {
      const { accessToken } = dataContext.state
      setIsSubmitting(true)
      if (data.selectedQuestion.id) {
         await api(accessToken).questionDelete(data.selectedQuestion.id)
      }
      const filteredQuestions = data.questions.filter((x) => x.id !== data.selectedQuestion.id)

      reset((prev) => {
         const updatedSelectedQuestionIndex = filteredQuestions.findIndex((x) => x.id === data.selectedQuestion.id)
         const updatedSelectedQuestion =
            updatedSelectedQuestionIndex >= 0
               ? filteredQuestions[updatedSelectedQuestionIndex]
               : filteredQuestions.length === 0
               ? null
               : filteredQuestions[filteredQuestions.length - 1]
         return {
            questions: filteredQuestions,
            selectedQuestion: updatedSelectedQuestion,
         }
      })
      updateQuestionsInState(dataContext, filteredQuestions, demand, lvlOneParentDemandId)
      dataContext.handleMessage(dataContext.setRootState, 'success', t('ItemDeleted'), t('QuestionDeleted'))
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('DeleteError'), error.message)
   } finally {
      setIsSubmitting(false)
   }
}

export const addQuestion = async (
   dataContext: IDataContext,
   questionForm: UseFormReturn<QuestionFormValues, any, undefined>,
   setOpenModal: React.Dispatch<React.SetStateAction<'new' | 'undo' | 'badgeUndo'>>,
   name: string,
   demand: Demand,
   lvlOneParentDemandId: string
): Promise<void> => {
   const { accessToken } = dataContext.state
   const { reset, getValues } = questionForm
   const newQuestion = (await api(accessToken).questionCreate(getNewQuestion(name, demand.id))).data
   const questions = [...getValues().questions.sort((a, b) => a.order - b.order), newQuestion]
   reset((prev) => ({
      selectedQuestion: newQuestion,
      questions,
   }))
   updateQuestionsInState(dataContext, questions, demand, lvlOneParentDemandId)
   setOpenModal(null)
}

export const getMatchingQuestions = (questions: Question[], question: Question, parentDemand: Demand): Question[] => {
   const hasMatching = (types1: string[], types2: string[]): boolean => {
      return types1.some((type1) => types2.some((type2) => type1 === type2))
   }
   if (parentDemand.level === DemandLevel.Three) return []

   return questions
      .filter((x) => x.id !== question.id)
      .filter((existingQuestion) => {
         return (
            hasMatching(
               existingQuestion.organisationTypes.map((x) => x.organisationTypeId),
               question.organisationTypes.map((x) => x.organisationTypeId)
            ) &&
            hasMatching(
               existingQuestion.organisationSizes.map((x) => x.organisationSizeId),
               question.organisationSizes.map((x) => x.organisationSizeId)
            ) &&
            hasMatching(
               existingQuestion.snIs.map((x) => x.sniId),
               question.snIs.map((x) => x.sniId)
            )
         )
      })
}
const updateQuestionsInState = (dataContext: IDataContext, updatedQuestions: Question[], demand: Demand, grandparentId: string) => {
   const { demands } = dataContext.state
   const updatedLvlOneParentDemand = demands.find((x) => x.id === grandparentId)

   const setQuestions = (target: Demand) => (target.questions = updatedQuestions)

   if (demand.level === DemandLevel.One) {
      updatedLvlOneParentDemand.questions = updatedQuestions
   } else if (demand.level === DemandLevel.Two) {
      const child = updatedLvlOneParentDemand.children.find((x) => x.id === demand.id)
      setQuestions(child)
   } else {
      const child = updatedLvlOneParentDemand.children.find((x) => x.id === demand.parentId)
      const grandChild = child.children.find((x) => x.id === demand.id)
      setQuestions(grandChild)
   }

   UpdateItemInCollection(dataContext.setRootState, demands, updatedLvlOneParentDemand, 'demandsLvlOne')
}

export const getQuestions = async (dataContext: IDataContext, page: number, setQuestions: React.Dispatch<React.SetStateAction<QuestionDTOPaginatedList>>) => {
   try {
      const { accessToken } = dataContext.state
      const response = (await api(accessToken).questionPaginatedList({ pageIndex: page, pageSize: 20 })).data
      setQuestions(response)
   } catch (error: any) {
      dataContext.handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchQuestions'), error.message)
   }
}
export const getQuestionsByDemandId = async (demandId: string, accessToken: string): Promise<QuestionDTO[]> => {
   try {
      const questions = (await api(accessToken).getQuestionsByDemandId(demandId)).data
      return questions.sort((a, b) => a.order - b.order)
   } catch (error) {
      throw error
   }
}

export const getAllQuestions = async (accessToken: string): Promise<QuestionDTO[]> => {
   try {
      const questions = (await api(accessToken).questionList()).data
      return questions.sort((a, b) => a.order - b.order)
   } catch (error) {
      throw error
   }
}
